import { useEffect, useRef, useState } from 'react'
import type { Editor } from '@tiptap/react'
import { useLang } from '../i18n'
import { numberLevels } from '../lib/sections'
import { pmHeadings } from './editor/pm-sections'
import type { SectionLock } from './editor/SectionNumbers'

/** Re-derive the outline at most this often — a heading keystroke fires a transaction per char. */
const REFRESH_MS = 150

/** The panel opens at most this share of the editor's visible height, then scrolls. */
export const OUTLINE_CAP = 0.25
/** Dragging the bottom edge never shrinks the panel below this (px)… */
export const OUTLINE_MIN_PX = 60
/** …nor grows it past this share of the editor's visible height. */
export const OUTLINE_MAX = 0.9
/** One keyboard step on the resize handle (px). */
const RESIZE_STEP_PX = 16

/** One row of the outline. `from` is a live ProseMirror position, re-derived on every refresh. */
interface OutlineRow {
  number: string
  title: string
  from: number
  /** Who holds this § right now, if anyone (display name). */
  lockedBy: string | null
}

/**
 * The outline is derived from the SAME source as the inline numbers — `pmHeadings(doc)` fed to
 * the shared `numberLevels` — so the number a member reads in the panel and the one they read
 * on the heading cannot drift apart, and both equal the number the backend resolves.
 */
function readOutline(editor: Editor, locks: SectionLock[], offset: number | null): OutlineRow[] {
  // Not known yet (see SectionNumbers.ts) — an empty outline for a moment beats numbering it
  // under a guessed 0 and re-numbering every row a beat later.
  if (offset === null) return []
  // A destroyed editor (tiptap tears instances down on its own timer) has no outline to read.
  if (editor.isDestroyed) return []
  const heads = pmHeadings(editor.state.doc)
  const numbers = numberLevels(
    heads.map((h) => h.level),
    offset,
  )
  return heads.map((h, idx) => {
    const number = numbers[idx]!
    // '' locks the whole part, so it holds every § in it.
    const lock = locks.find((l) => l.section === number || l.section === '')
    return { number, title: h.title, from: h.from, lockedBy: lock ? lock.actorName : null }
  })
}

interface SectionOutlineProps {
  /** The live editor (null until the document is open) — the single source of the outline. */
  editor: Editor | null
  /** The § someone holds right now, so a held row is announced as well as marked. */
  locks: SectionLock[]
  /** Where this part's numbering starts inside its artefact — parts continue the run rather
   *  than restarting at 1, so part 2's outline opens at §3. Supplied by the backend; `null`
   *  while the host hasn't heard back yet. */
  offset: number | null
  /** Copy a paste-ready reference to a § — the MVP-1 "hand it to your console agent" flow. */
  onCopyRef: (number: string, title: string) => void
  /** Matches the toggle's `aria-controls`. */
  id?: string
  /** The editor's visible height (px, the scrollport the panel sticks in); 0 = not measured yet.
   *  The panel opens capped at OUTLINE_CAP of it, and a drag stays within OUTLINE_MAX of it. */
  editorHeight?: number
}

/**
 * The collapsible § outline, rendered as a full-width disclosure panel inside the canvas.
 * (`.workspace` is a fixed 3-column grid with no room for a fourth rail, so a persistent
 * outline rail is deferred; this is correct at every width and changes no layout.)
 *
 * Clicking a row jumps through `view.nodeDOM(from)` rather than `coordsAtPos`: a cached
 * rectangle is stale the moment a remote update reparses the document, whereas nodeDOM
 * resolves the element that currently represents that position.
 */
export function SectionOutline({ editor, locks, offset, onCopyRef, id, editorHeight = 0 }: SectionOutlineProps) {
  const { t } = useLang()
  const [rows, setRows] = useState<OutlineRow[]>([])
  // The height the member dragged the bottom edge to; null = never resized, so the cap applies.
  const [height, setHeight] = useState<number | null>(null)
  const navRef = useRef<HTMLElement>(null)
  // Detaches an in-flight drag's window listeners — also on unmount, should the panel close mid-drag.
  const stopDrag = useRef<(() => void) | null>(null)
  useEffect(() => () => stopDrag.current?.(), [])

  // The panel is sticky under the toolbar, so a heading jumped to must clear it as well: publish
  // its live height as --sec-outline-h on the parent (the canvas), which the headings'
  // scroll-margin-top reads. Removed again when the panel closes.
  useEffect(() => {
    const nav = navRef.current
    const host = nav?.parentElement
    if (!nav || !host || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => {
      host.style.setProperty('--sec-outline-h', `${nav.offsetHeight}px`)
    })
    observer.observe(nav)
    return () => {
      observer.disconnect()
      host.style.removeProperty('--sec-outline-h')
    }
  }, [])

  const maxPx = editorHeight > 0 ? Math.round(editorHeight * OUTLINE_MAX) : Math.round(window.innerHeight * OUTLINE_MAX)
  const clamp = (px: number): number => Math.round(Math.min(Math.max(px, OUTLINE_MIN_PX), Math.max(maxPx, OUTLINE_MIN_PX)))
  /** What the panel measures right now — the dragged height, else its rendered one. */
  const currentHeight = (): number => height ?? navRef.current?.getBoundingClientRect().height ?? OUTLINE_MIN_PX

  function startResize(e: React.PointerEvent<HTMLDivElement>): void {
    if (e.button !== 0) return
    e.preventDefault()
    const startY = e.clientY
    const startH = currentHeight()
    const onMove = (ev: PointerEvent): void => setHeight(clamp(startH + ev.clientY - startY))
    const onUp = (): void => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
      stopDrag.current = null
    }
    stopDrag.current = onUp
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
  }

  function resizeByKey(e: React.KeyboardEvent<HTMLDivElement>): void {
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return
    e.preventDefault()
    setHeight(clamp(currentHeight() + (e.key === 'ArrowDown' ? RESIZE_STEP_PX : -RESIZE_STEP_PX)))
  }

  // Never resized: capped at a quarter of the editor (CSS falls back to 25vh until it is measured).
  // Resized: exactly the dragged height, the cap no longer applies.
  const sizeStyle: React.CSSProperties =
    height !== null
      ? { height, maxHeight: 'none' }
      : editorHeight > 0
        ? { maxHeight: Math.round(editorHeight * OUTLINE_CAP) }
        : {}

  useEffect(() => {
    if (!editor || editor.isDestroyed) {
      setRows([])
      return
    }
    setRows(readOutline(editor, locks, offset))
    let timer: ReturnType<typeof setTimeout> | undefined
    const schedule = (): void => {
      clearTimeout(timer)
      timer = setTimeout(() => setRows(readOutline(editor, locks, offset)), REFRESH_MS)
    }
    editor.on('transaction', schedule)
    return () => {
      clearTimeout(timer)
      editor.off('transaction', schedule)
    }
  }, [editor, locks, offset])

  function jumpTo(row: OutlineRow): void {
    if (!editor || editor.isDestroyed) return
    const dom = editor.view.nodeDOM(row.from)
    if (dom instanceof HTMLElement) dom.scrollIntoView({ block: 'start', behavior: 'smooth' })
    // Place the caret inside the heading so keyboard users land where the page scrolled to.
    // Scrolling is already done above — asking focus() to scroll again would fight the smooth one.
    editor.chain().focus(row.from + 1, { scrollIntoView: false }).run()
  }

  return (
    <nav ref={navRef} className="sec-outline" id={id} aria-label={t('sec_nav')} style={sizeStyle}>
      <div className="sec-scroll">
        {rows.length === 0 ? (
          <p className="sec-empty">{t('sec_none')}</p>
        ) : (
          <ol>
            {rows.map((row) => (
              // Indent by outline depth: "3.2.4" is three levels in.
              <li key={`${row.number}-${row.from}`} style={{ paddingLeft: `${(row.number.split('.').length - 1) * 18}px` }}>
                <button
                  type="button"
                  className="sec-row"
                  title={`${t('sec_go')} ${row.number}`}
                  onClick={() => jumpTo(row)}
                >
                  <span className="n">{row.number}</span>
                  <span className="ttl">{row.title}</span>
                  {row.lockedBy && (
                    <span className="lk">
                      <span aria-hidden="true">🔒</span>
                      {/* Announce the lock — shading alone would be colour-only (WCAG 1.4.1). */}
                      <span className="visually-hidden">, {t('sec_locked_by')} {row.lockedBy}</span>
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  className="sec-copy"
                  aria-label={`${t('sec_copy_ref')} ${row.number}${row.title ? ` — ${row.title}` : ''}`}
                  onClick={() => onCopyRef(row.number, row.title)}
                >
                  <span aria-hidden="true">⧉</span>
                </button>
              </li>
            ))}
          </ol>
        )}
      </div>
      {/* The draggable bottom edge. A focusable separator is a widget: it states its value and
          answers the arrow keys, so the resize is not pointer-only (WCAG 2.1.1). */}
      <div
        className="sec-resize"
        role="separator"
        aria-orientation="horizontal"
        aria-label={t('sec_resize')}
        aria-controls={id}
        aria-valuemin={OUTLINE_MIN_PX}
        aria-valuemax={Math.max(maxPx, OUTLINE_MIN_PX)}
        aria-valuenow={Math.round(height ?? (editorHeight > 0 ? editorHeight * OUTLINE_CAP : OUTLINE_MIN_PX))}
        tabIndex={0}
        onPointerDown={startResize}
        onKeyDown={resizeByKey}
      />
    </nav>
  )
}
